import { useState, useEffect, useCallback, useRef } from "react";
import { Order } from "@/redux/services/order/order.type";
import { useAppSelector } from "@/redux/features/hooks";
import { playNotificationSound } from "@/lib/audioUtils";
import { Roles } from "@/redux/services/user/user.type";
import { logger } from "@/services/Logger";

export const useOrderNotifications = (
  orders: Order[] = [],
  interval: number = 60000
) => {
  const [lastNotificationTime, setLastNotificationTime] = useState<number>(0);
  const [prevPendingCount, setPrevPendingCount] = useState<number>(0);
  const [prevModifiedCount, setPrevModifiedCount] = useState<number>(0);
  const initialCheckDone = useRef(false);
  const isNotifying = useRef(false);
  const ordersRef = useRef(orders);

  const shouldAllowNotifications = useRef(false);

  const { currentRestaurant, currentUser } = useAppSelector(
    (state) => state.user
  );

  const canReceiveNotifications = useCallback(() => {
    return (
      currentUser?.roles?.some((role) =>
        [Roles.cashier, Roles.waiter].includes(role)
      ) || false
    );
  }, [currentUser]);

  const pendingOrdersCount = useCallback(() => {
    return ordersRef.current.filter((order) => order.status === "pending")
      .length;
  }, []);

  const modifiedOrdersCount = useCallback(() => {
    return ordersRef.current.filter((order) => order.isModified === true)
      .length;
  }, []);

  useEffect(() => {
    ordersRef.current = orders;

    if (initialCheckDone.current) {
      const currentPendingCount = pendingOrdersCount();
      const currentModifiedCount = modifiedOrdersCount();

      setPrevPendingCount(currentPendingCount);
      setPrevModifiedCount(currentModifiedCount);
    }
  }, [orders, pendingOrdersCount, modifiedOrdersCount]);

  const playOrderNotification = useCallback(
    async (forcePlay = false) => {
      if (isNotifying.current) {
        return;
      }

      const now = Date.now();
      const currentPendingCount = pendingOrdersCount();
      const currentModifiedCount = modifiedOrdersCount();

      if (currentPendingCount === 0 && currentModifiedCount === 0) {
        return;
      }

      const shouldNotify =
        (forcePlay ||
          !initialCheckDone.current ||
          shouldAllowNotifications.current) &&
        (currentPendingCount > 0 || currentModifiedCount > 0);

      if (shouldNotify && canReceiveNotifications()) {
        isNotifying.current = true;
        initialCheckDone.current = true;
        shouldAllowNotifications.current = false;

        await playNotificationSound();

        setLastNotificationTime(now);
        setPrevPendingCount(currentPendingCount);
        setPrevModifiedCount(currentModifiedCount);

        if ("Notification" in window && Notification.permission === "granted") {
          try {
            if ("serviceWorker" in navigator) {
              const registration = await navigator.serviceWorker.ready;
              await registration.showNotification("Attention requise", {
                body: `${
                  currentPendingCount > 0
                    ? `${currentPendingCount} commande(s) en attente. `
                    : ""
                }${
                  currentModifiedCount > 0
                    ? `${currentModifiedCount} commande(s) modifiée(s).`
                    : ""
                }`,
                icon: "/favicon.ico",
                tag: "orders-notification",
              });
            } else {
              new Notification("Attention requise", {
                body: `${
                  currentPendingCount > 0
                    ? `${currentPendingCount} commande(s) en attente. `
                    : ""
                }${
                  currentModifiedCount > 0
                    ? `${currentModifiedCount} commande(s) modifiée(s).`
                    : ""
                }`,
                icon: "/favicon.ico",
                tag: "orders-notification",
              });
            }
          } catch (err) {
            logger.error("useOrderNotifications", "Failed to show notification:", err);
          }
        }

        setTimeout(() => {
          isNotifying.current = false;
        }, 1000);
      }
    },
    [
      canReceiveNotifications,
      lastNotificationTime,
      pendingOrdersCount,
      modifiedOrdersCount,
      prevPendingCount,
      prevModifiedCount,
    ]
  );

  useEffect(() => {
    if (!canReceiveNotifications() || !currentRestaurant) return;

    const initialCheckTimeout = setTimeout(() => {
      if (!initialCheckDone.current) {
        playOrderNotification(true);
      }
    }, 2000);

    const notificationInterval = setInterval(() => {
      shouldAllowNotifications.current = true;
      playOrderNotification();
    }, interval);

    return () => {
      clearTimeout(initialCheckTimeout);
      clearInterval(notificationInterval);
    };
  }, [
    canReceiveNotifications,
    currentRestaurant,
    playOrderNotification,
    interval,
  ]);

  return {
    hasPendingOrders: pendingOrdersCount() > 0,
    pendingOrdersCount: pendingOrdersCount(),
    hasModifiedOrders: modifiedOrdersCount() > 0,
    modifiedOrdersCount: modifiedOrdersCount(),
  };
};
